import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { useFormContext, useWatch } from "@swa-ui/form";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import {
  ApartmentSuiteFormField,
  apartmentSuiteFormFieldPropTypes,
} from "../ApartmentSuiteFormField";
import { CityFormField, cityFormFieldPropTypes } from "../CityFormField";
import {
  COUNTRY_SCHEMA_FIELD_NAME,
  CountryFormField,
  countryFormFieldPropTypes,
} from "../CountryFormField";
import {
  STATE_SCHEMA_FIELD_NAME,
  StateFormField,
  stateFormFieldPropTypes,
} from "../StateFormField";
import { StateProvinceRegionFormField } from "../StateProvinceRegionFormField";
import { StreetAddressFormField, streetAddressFormFieldPropTypes } from "../StreetAddressFormField";
import {
  UNITED_STATES_COUNTRY_CODE,
  ZIPCODE_SCHEMA_FIELD_NAME,
  ZipCodeFormField,
  zipCodeFormFieldPropTypes,
} from "../ZipCodeFormField";
import styles from "./AddressFormFields.module.scss";

export const AddressFormFields = (props) => {
  const {
    apartmentSuiteFormFieldProps = {
      description: i18n("AddressFormField__OPTIONAL"),
    },
    cityFormFieldProps = {},
    className,
    countryFormFieldProps = {},
    stateFormFieldProps = {},
    streetAddressFormFieldProps = {},
    zipCodeFormFieldProps = {},
  } = props;
  const [showZipCodeField, setShowZipCodeField] = useState(true);
  const isInitialCountrySelection = useRef(true);
  const { setValue, clearErrors, trigger } = useFormContext();
  const countriesData = getBootstrapData("countries-data");
  const countrySchemaFieldName = countryFormFieldProps.name ?? COUNTRY_SCHEMA_FIELD_NAME;
  const stateSchemaFieldName = stateFormFieldProps.name ?? STATE_SCHEMA_FIELD_NAME;
  const zipCodeSchemaFieldName = zipCodeFormFieldProps.name ?? ZIPCODE_SCHEMA_FIELD_NAME;
  const selectedCountry =
    useWatch({ name: countrySchemaFieldName }) ??
    countryFormFieldProps?.componentProps?.defaultValue;

  useEffect(() => {
    if (countriesData && selectedCountry) {
      const { postalCodeRequired } =
        countriesData?.find(({ countryCode }) => countryCode === selectedCountry) || {};

      setValue(zipCodeSchemaFieldName, "");
      clearErrors([stateSchemaFieldName, zipCodeSchemaFieldName]);

      if (postalCodeRequired && !isInitialCountrySelection.current) {
        trigger(zipCodeSchemaFieldName);
      }
      setShowZipCodeField(postalCodeRequired);
      isInitialCountrySelection.current = false;
    }
  }, [countriesData, selectedCountry]);

  return (
    <div className={className}>
      <CountryFormField {...countryFormFieldProps} />
      <div className={styles.streetAddressFields}>
        <StreetAddressFormField {...streetAddressFormFieldProps} />
        <ApartmentSuiteFormField {...apartmentSuiteFormFieldProps} />
      </div>
      <div className={getZipCodeClass()}>
        <CityFormField {...cityFormFieldProps} />
        {renderStateField()}
        {renderZipCodeField()}
      </div>
    </div>
  );

  function renderStateField() {
    const StateFieldComponent = isNonUSCountry() ? StateProvinceRegionFormField : StateFormField;

    return <StateFieldComponent {...stateFormFieldProps} />;
  }

  function renderZipCodeField() {
    return showZipCodeField ? <ZipCodeFormField {...getZipCodeProps()} /> : null;
  }

  function isNonUSCountry() {
    return selectedCountry && selectedCountry !== UNITED_STATES_COUNTRY_CODE;
  }

  function getZipCodeClass() {
    return classNames({
      [styles.cityStateZipFields]: showZipCodeField,
      [styles.cityStateFields]: !showZipCodeField,
    });
  }

  function getZipCodeProps() {
    return {
      ...zipCodeFormFieldProps,
      selectedCountry,
    };
  }
};

export const addressFormFieldsPropTypes = {
  /** Props for the apartment/suite form field. */
  apartmentSuiteFormFieldProps: PropTypes.shape(apartmentSuiteFormFieldPropTypes),

  /** Props for the city form field. */
  cityFormFieldProps: PropTypes.shape(cityFormFieldPropTypes),

  /** Class name for positioning the component. */
  className: PropTypes.string,

  /** Props for the country form field. */
  countryFormFieldProps: PropTypes.shape(countryFormFieldPropTypes),

  /** Props for the state form field. */
  stateFormFieldProps: PropTypes.shape(stateFormFieldPropTypes),

  /** Props for the street address form field. */
  streetAddressFormFieldProps: PropTypes.shape(streetAddressFormFieldPropTypes),

  /** Props for the zipCode form field. */
  zipCodeFormFieldProps: PropTypes.shape(zipCodeFormFieldPropTypes),
};

AddressFormFields.propTypes = addressFormFieldsPropTypes;
